import { isStaffUser, isAccountAdminUser, isSuperAdminUser, isConsultantUser, isSupervisor } from "./roles";

/**
 * Staff Training is unused — hide from UI everywhere until this is flipped back to true.
 * Nav, home cards, reports, and settings all key off this (via staffTraining flag checks).
 */
export const STAFF_TRAINING_UI_ENABLED = false;

/**
 * Mapping of navigation item titles to feature flag names
 */
const FEATURE_FLAG_MAPPING = {
	// Info section
	'Hazard Library': 'incidents', // Part of Incidents feature
	'Hazardous Substance Library': 'incidents', // Part of Incidents feature
	'Staff Training Topics': 'staffTraining', // Part of Staff Training feature
	'Job Safety Analysis (JSA)': 'jsa',
	'Site Safety Plans (Checklist)': 'siteSafetyPlans',
	// Site details quick links (titles differ from main nav in places)
	Hazards: 'incidents',
	'Hazardous Substances': 'incidents',
	'Site Safety Plans (SSPs)': 'siteSafetyPlans',
	Inductions: 'inductionMeetings',

	// Activity section
	'Tasks': 'tasks',
	'My Tasks': 'tasks',
	'Incidents': 'incidents',
	'Induction Meetings': 'inductionMeetings',
	'Safety Minutes': 'safetyMinutes',
	'Staff Trainings': 'staffTraining',
	'Site Inspection Checklists': 'siteInspections',
	'Leave': 'leave',
	'Equipment Usage': 'equipmentUsageRecording',

	// Admin section
	'Manage Work Types': 'workTypes',
	'Manage Leave Types': 'leave',
	'Equipment': 'equipment',
};

/**
 * Filters navigation items based on feature flags
 * @param {Array} pages - Array of navigation pages
 * @param {Object} featureFlags - Feature flags object from Redux state
 * @returns {Array} Filtered navigation pages
 */
export const filterNavigationByFeatureFlags = (pages, featureFlags) => {
	if (!pages || !Array.isArray(pages)) {
		return [];
	}

	return pages.map(page => {
		// If page has children, filter them recursively
		if (page.children && Array.isArray(page.children)) {
			const filteredChildren = filterNavigationByFeatureFlags(page.children, featureFlags);
			
			// Check if this page itself should be visible
			const shouldShow = shouldShowPageForFeatureFlags(page, featureFlags);
			
			if (!shouldShow || filteredChildren.length === 0) {
				return null;
			}
			
			return {
				...page,
				children: filteredChildren
			};
		}

		// Check if this specific page should be visible
		const shouldShow = shouldShowPageForFeatureFlags(page, featureFlags);
		
		if (shouldShow) {
			return page;
		}

		return null;
	}).filter(Boolean); // Remove null entries
};

/**
 * Determines if a specific page should be visible based on feature flags
 * @param {Object} page - Navigation page object
 * @param {Object} featureFlags - Feature flags object
 * @returns {boolean} Whether the page should be visible
 */
const shouldShowPageForFeatureFlags = (page, featureFlags) => {
	// Equipment usage requires both equipment and usage recording flags
	if (page.title === "Equipment Usage") {
		if (!featureFlags) {
			return true;
		}
		return (
			featureFlags.equipment === true &&
			featureFlags.equipmentUsageRecording === true
		);
	}

	// Check if this page has a feature flag mapping
	const featureFlagName = FEATURE_FLAG_MAPPING[page.title];

	// Staff Training temporarily hard-hidden (even before feature flags load)
	if (featureFlagName === 'staffTraining' && !STAFF_TRAINING_UI_ENABLED) {
		return false;
	}

	if (!featureFlags) {
		// If feature flags not loaded, show remaining pages (default to enabled)
		return true;
	}
	
	if (featureFlagName) {
		// Equipment, Tasks, and Staff Training default to false (disabled), so check for explicit true
		// Other features default to true (enabled), so check for not false
		if (
			featureFlagName === 'equipment' ||
			featureFlagName === 'equipmentUsageRecording' ||
			featureFlagName === 'tasks' ||
			featureFlagName === 'staffTraining'
		) {
			return featureFlags[featureFlagName] === true;
		}
		// Check if the feature is enabled (default to true if not set)
		const isEnabled = featureFlags[featureFlagName] !== false;
		return isEnabled;
	}

	// If no feature flag mapping, show the page (default behavior)
	return true;
};

/** Same rules as the main navigation: org Settings / feature flags per module title. */
export const isOrganisationModuleEnabled = (moduleTitle, featureFlags) =>
	shouldShowPageForFeatureFlags({ title: moduleTitle }, featureFlags);

/**
 * Filters navigation items based on user role
 * @param {Array} pages - Array of navigation pages
 * @param {string} role - User role
 * @param {{ supervisingStaffList?: string[] }} [visibility]
 * @returns {Array} Filtered navigation pages
 */
export const filterNavigationByRole = (pages, role, visibility = {}) => {
  if (!pages || !Array.isArray(pages)) {
    return [];
  }

  return pages
    .map((page) => {
      const hasChildren = !!page.children && Array.isArray(page.children);
      const shouldShow = shouldShowPageForRole(page, role, visibility);

      if (!shouldShow) {
        return null;
      }

      // If page has children, filter them recursively (WITHOUT mutating the source config)
      if (hasChildren) {
        let filteredChildren = filterNavigationByRole(page.children, role, visibility);

        // Additional staff-only section trimming rules (pure filtering)
        if (isStaffUser(role)) {
          if (page.title === "Info") {
            const staffAllowedInfoItems = [
              "Customers",
              "Sites",
              "Staff",
              "Equipment",
              "Hazard Library",
              "Hazardous Substance Library",
              "Documents",
            ];
            // Customer and site titles follow organisation terminology (for example "Jobs"),
            // so those two stay available by route even when the label is not the default.
            const staffAllowedInfoPaths = ["/customers", "/sites"];
            filteredChildren = filteredChildren.filter(
              (child) =>
                staffAllowedInfoItems.includes(child.title) ||
                staffAllowedInfoPaths.includes(child.href)
            );
          }

          if (page.title === "Admin") {
            const staffAllowedAdminItems = [
              "Profile",
              "Accounts", // Matches navigation-config title
              "Change Password",
            ];
            filteredChildren = filteredChildren.filter((child) =>
              staffAllowedAdminItems.includes(child.title)
            );
          }
        }

        // If nothing remains under this parent, hide the parent entry entirely
        if (filteredChildren.length === 0) {
          return null;
        }

        return {
          ...page,
          children: filteredChildren,
        };
      }

      return page;
    })
    .filter(Boolean); // Remove null entries
};

/**
 * Determines if a specific page should be visible for a given role
 * @param {Object} page - Navigation page object
 * @param {string} role - User role
 * @param {{ supervisingStaffList?: string[] }} [visibility]
 * @returns {boolean} Whether the page should be visible
 */
const shouldShowPageForRole = (page, role, visibility = {}) => {
  // Check if the page has explicit role restrictions
  if (page.restrictedRoles && Array.isArray(page.restrictedRoles)) {
    if (page.restrictedRoles.includes(role)) {
      return false; // Page is restricted for this role
    }
  }

  // Staff users have limited access
  if (isStaffUser(role)) {
    return isPageAllowedForStaff(page, visibility);
  }

  // Account administrators have most access except super admin features
  if (isAccountAdminUser(role)) {
    return isPageAllowedForAccountAdmin(page);
  }

  // Super admins have full access
  if (isSuperAdminUser(role)) {
    return true;
  }

  // Consultants have most access
  if (isConsultantUser(role)) {
    return isPageAllowedForConsultant(page);
  }

  // Default: show all pages
  return true;
};

/**
 * Determines if a page is allowed for Staff users
 * @param {Object} page - Navigation page object
 * @param {{ supervisingStaffList?: string[] }} [visibility]
 * @returns {boolean} Whether the page is allowed for staff
 */
const isPageAllowedForStaff = (page, visibility = {}) => {
  // IMPORTANT: Do not mutate `page` here. Child filtering is handled in `filterNavigationByRole`.

  // Staff with no direct reports do not get the staff directory.
  if (page.title === "Staff" && !isSupervisor(visibility.supervisingStaffList)) {
    return false;
  }

  // For Activity section, staff users can see all items
  if (page.title === 'Activity') {
    return true;
  }

  // Staff users can still see the Admin section (children will be filtered upstream)
  if (page.title === 'Admin') {
    return true;
  }

  // For Home, always show
  if (page.title === 'Home') {
    return true;
  }

  // For individual items, check if they're restricted
  const staffRestrictedPages = [
    'Invite Organisation',
    'Invite Consultant', 
    'Subscription',
    'Manage Staff Types',
    'Manage Work Types',
    'Accounts'
  ];

  const isRestricted = staffRestrictedPages.includes(page.title);
  return !isRestricted;
};

/**
 * Determines if a page is allowed for Account Administrators
 * @param {Object} page - Navigation page object
 * @returns {boolean} Whether the page is allowed for account admins
 */
const isPageAllowedForAccountAdmin = (page) => {
  const accountAdminRestrictedPages = [
    'Invite Consultant', // Only super admins can invite consultants
  ];

  return !accountAdminRestrictedPages.includes(page.title);
};

/**
 * Determines if a page is allowed for Consultants
 * @param {Object} page - Navigation page object
 * @returns {boolean} Whether the page is allowed for consultants
 */
const isPageAllowedForConsultant = (page) => {
  // Consultants have similar access to account admins
  return isPageAllowedForAccountAdmin(page);
};

/**
 * Gets the appropriate navigation items for a specific role
 * @param {Array} navigationConfig - Full navigation configuration
 * @param {string} role - User role
 * @returns {Array} Filtered navigation configuration
 */
export const getNavigationForRole = (navigationConfig, role) => {
  if (!navigationConfig || !Array.isArray(navigationConfig)) {
    return [];
  }

  return navigationConfig.map(config => ({
    ...config,
    pages: filterNavigationByRole(config.pages, role)
  }));
};

/**
 * Gets the appropriate navigation items filtered by both role and feature flags
 * @param {Array} navigationConfig - Full navigation configuration
 * @param {string} role - User role
 * @param {Object} featureFlags - Feature flags object from Redux state
 * @returns {Array} Filtered navigation configuration
 */
export const getNavigationForRoleAndFeatures = (navigationConfig, role, featureFlags) => {
  if (!navigationConfig || !Array.isArray(navigationConfig)) {
    return [];
  }

  return navigationConfig.map(config => {
    // First filter by role
    const roleFilteredPages = filterNavigationByRole(config.pages, role);
    // Then filter by feature flags
    const featureFilteredPages = filterNavigationByFeatureFlags(roleFilteredPages, featureFlags);
    
    return {
      ...config,
      pages: featureFilteredPages
    };
  });
};
